import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { User, Mail, Phone, MapPin, Shield, CheckCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { countries } from '../mockData';

const MonCompte = () => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    country: user?.country || 'ci',
    gender: user?.gender || '',
    address: user?.address || ''
  });

  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleProfileChange = (e) => {
    setProfileData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handlePasswordChange = (e) => {
    setPasswordData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      updateUser(profileData);
      setIsEditing(false);
      setIsSaving(false);
      toast({
        title: "✅ Profil mis à jour",
        description: "Vos informations ont été enregistrées"
      });
    }, 1000);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas",
        variant: "destructive"
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast({
        title: "Erreur",
        description: "Le mot de passe doit contenir au moins 6 caractères",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setIsChangingPassword(false);
      setIsSaving(false);
      toast({
        title: "✅ Mot de passe modifié",
        description: "Votre mot de passe a été mis à jour"
      });
    }, 1000);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-xl md:text-3xl font-bold text-gray-900">Mon Compte</h1>
        <p className="text-gray-500 mt-1">Gérez vos informations personnelles et vos paramètres</p>
      </div>

      {/* Informations personnelles */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle>Informations personnelles</CardTitle>
                <CardDescription>Vos informations de compte et de contact</CardDescription>
              </div>
            </div>
            {!isEditing && (
              <Button 
                onClick={() => setIsEditing(true)}
                variant="outline"
                className="border-[#1db584] text-[#1db584] hover:bg-green-50"
              >
                Modifier
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Prénom</Label>
              <Input
                name="firstName"
                value={profileData.firstName}
                onChange={handleProfileChange}
                disabled={!isEditing}
                className="focus:border-[#1db584] focus:ring-[#1db584]"
              />
            </div>
            <div className="space-y-2">
              <Label>Nom</Label>
              <Input
                name="lastName"
                value={profileData.lastName}
                onChange={handleProfileChange}
                disabled={!isEditing}
                className="focus:border-[#1db584] focus:ring-[#1db584]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={user?.email}
                disabled
                className="pl-10 bg-gray-50"
              />
            </div>
            <p className="text-xs text-gray-500">L'email ne peut pas être modifié</p>
          </div>

          <div className="space-y-2">
            <Label>Téléphone</Label>
            <div className="flex gap-2">
              <Select 
                value={profileData.country} 
                onValueChange={(value) => setProfileData(prev => ({ ...prev, country: value }))}
                disabled={!isEditing}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country) => (
                    <SelectItem key={country.value} value={country.value}>
                      {country.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                name="phone"
                value={profileData.phone}
                onChange={handleProfileChange}
                disabled={!isEditing}
                className="flex-1 focus:border-[#1db584] focus:ring-[#1db584]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Genre</Label>
            <Select 
              value={profileData.gender} 
              onValueChange={(value) => setProfileData(prev => ({ ...prev, gender: value }))}
              disabled={!isEditing}
            >
              <SelectTrigger className="focus:border-[#1db584] focus:ring-[#1db584]">
                <SelectValue placeholder="Sélectionnez" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="homme">Homme</SelectItem>
                <SelectItem value="femme">Femme</SelectItem>
                <SelectItem value="autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Adresse</Label>
            <Input
              name="address"
              value={profileData.address}
              onChange={handleProfileChange}
              disabled={!isEditing}
              placeholder="Votre adresse complète"
              className="focus:border-[#1db584] focus:ring-[#1db584]"
            />
          </div>

          {isEditing && (
            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="bg-gradient-to-r from-[#1db584] to-[#16a075] hover:from-[#16a075] hover:to-[#128f69] text-white"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  'Enregistrer'
                )}
              </Button>
              <Button
                onClick={() => {
                  setIsEditing(false);
                  setProfileData({
                    firstName: user?.firstName || '',
                    lastName: user?.lastName || '',
                    phone: user?.phone || '',
                    country: user?.country || 'ci',
                    gender: user?.gender || '',
                    address: user?.address || ''
                  });
                }}
                variant="outline"
              >
                Annuler
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Changer le mot de passe */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <Shield className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <CardTitle>Changer le mot de passe</CardTitle>
              <CardDescription>Modifiez votre mot de passe pour sécuriser votre compte</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!isChangingPassword ? (
            <Button
              onClick={() => setIsChangingPassword(true)}
              variant="outline"
              className="border-[#1db584] text-[#1db584] hover:bg-green-50"
            >
              Modifier mon mot de passe
            </Button>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label>Ancien mot de passe</Label>
                <Input
                  name="oldPassword"
                  type="password"
                  value={passwordData.oldPassword}
                  onChange={handlePasswordChange}
                  required
                  className="focus:border-[#1db584] focus:ring-[#1db584]"
                />
              </div>
              <div className="space-y-2">
                <Label>Nouveau mot de passe</Label>
                <Input
                  name="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  className="focus:border-[#1db584] focus:ring-[#1db584]"
                />
                <p className="text-xs text-gray-500">Le mot de passe doit contenir au moins 6 caractères</p>
              </div>
              <div className="space-y-2">
                <Label>Confirmer le nouveau mot de passe</Label>
                <Input
                  name="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  className="focus:border-[#1db584] focus:ring-[#1db584]"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="bg-gradient-to-r from-[#1db584] to-[#16a075] hover:from-[#16a075] hover:to-[#128f69] text-white"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Modification...
                    </>
                  ) : (
                    'Changer le mot de passe'
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setIsChangingPassword(false);
                    setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  variant="outline"
                >
                  Annuler
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Statut du compte */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <CardTitle>Statut du compte</CardTitle>
              <CardDescription>Informations sur votre compte Baobab Connect</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Statut</p>
              <Badge variant="success" className="mt-1">
                <CheckCircle className="w-3 h-3 mr-1" />
                Compte vérifié
              </Badge>
            </div>
            <div>
              <p className="text-sm text-gray-500">Type de compte</p>
              <p className="font-medium mt-1 capitalize">{user?.accountType || 'Particulier'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Mode de paiement préféré</p>
              <p className="font-medium mt-1">Mobile Money</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Membre depuis</p>
              <p className="font-medium mt-1">
                {new Date(user?.createdAt || Date.now()).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MonCompte;
